import React, { useState, useEffect } from 'react';
import { useTasks } from '../../hooks/useTasks';
import { useTaskTags } from '../../hooks/tasks/useTaskTags';
import { useTaskStatuses } from '../../hooks/tasks/useTaskStatuses';
import { useTaskFilters } from '../../hooks/tasks/useTaskFilters';
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import { Form, Button } from 'react-bootstrap';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';

import './Tasks.css';
import { TaskCard, SortableTaskCard } from './TaskCard';
import TagManager from './TagManager';
import TasksToolbar from './TasksToolbar';

const INDENT_THRESHOLD_PX = 40;

// In-list drop indicator shown at the projected landing position during indent/outdent drags
const DropIndicatorLine = ({ depth, label }) => (
    <div
        className="drop-indicator-line"
        style={{ marginLeft: depth > 0 ? '40px' : '0px' }}
    >
        <div className="drop-indicator-bar" />
        {label && <span className="drop-indicator-label">{label}</span>}
    </div>
);

const TaskGroupContainer = ({ id, children }) => {
    const { setNodeRef } = useDroppable({ id });
    return (
        <div ref={setNodeRef} className="d-flex flex-column gap-0 bg-white border rounded overflow-hidden shadow-sm" style={{ minHeight: '50px' }}>
            {children}
        </div>
    );
};

const TaskListView = ({ auth, workspaceId, selectedView, members = [] }) => {
    const project = selectedView?.type === 'project' ? selectedView.data : null;

    const { tasks, taskGroups, fetchTasks, createTask, updateTask, createTaskGroup, reorderTasks, assignTag, removeTag, deleteTask, addReminder, deleteReminder, updateProject, updateTaskGroup, reorderTaskGroups, deleteTaskGroup } = useTasks({
        token: auth?.user?.access_token,
        workspaceId
    });

    // Tags
    const { tags, fetchTags, createTag, updateTag, deleteTag } = useTaskTags({
        token: auth?.user?.access_token,
        workspaceId
    });

    const { taskStatuses, fetchStatuses, createStatus, updateStatus, deleteStatus } = useTaskStatuses({
        token: auth?.user?.access_token,
        workspaceId
    });

    // View State
    const [filterTags, setFilterTags] = useState([]); // Array of tag IDs

    const [newTaskContent, setNewTaskContent] = useState("");
    const [newTaskGroupName, setNewTaskGroupName] = useState("");
    const [isAddingTaskGroup, setIsAddingTaskGroup] = useState(false);
    const [isManageTags, setIsManageTags] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [dragDelta, setDragDelta] = useState(0);

    // Group Editing State
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editingGroupName, setEditingGroupName] = useState("");

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Use extracted filter hook
    const { filteredTasks, tasksByGroup } = useTaskFilters(tasks, taskGroups, filterTags);

    useEffect(() => {
        if (project?.id) {
            loadTasks();
        }
    }, [project?.id]);

    useEffect(() => {
        if (workspaceId) {
            fetchTags();
            fetchStatuses();
        }
    }, [workspaceId]);

    const loadTasks = async () => {
        if (project?.id) {
            await Promise.all([
                fetchTasks(project.id),
                fetchStatuses()
            ]);
        }
    };

    const handleCreateTask = async (e, taskGroupId = null, parentTaskId = null) => {
        if (e) e.preventDefault();
        let content = newTaskContent;
        if (parentTaskId) {
            const result = window.prompt("Enter subtask name:");
            if (!result) return;
            content = result;
        } else {
            if (!newTaskContent.trim()) return;
        }

        try {
            await createTask({
                projectId: project.id,
                taskGroupId: taskGroupId,
                parentTaskId: parentTaskId,
                content: content
            });
            if (!parentTaskId) setNewTaskContent("");
        } catch (err) { }
    };

    const handleCreateTaskGroup = async (e) => {
        e.preventDefault();
        if (!newTaskGroupName.trim()) return;
        try {
            await createTaskGroup(project.id, newTaskGroupName);
            setNewTaskGroupName("");
            setIsAddingTaskGroup(false);
        } catch (err) { }
    };

    const startEditingGroup = (group) => {
        setEditingGroupId(group.id);
        setEditingGroupName(group.name);
    };

    const saveEditingGroup = async () => {
        if (editingGroupId && editingGroupName.trim()) {
            await updateTaskGroup(editingGroupId, { name: editingGroupName });
        }
        setEditingGroupId(null);
        setEditingGroupName("");
    };

    // Section reorder: move a section up or down
    const handleMoveSection = (groupId, direction) => {
        const currentIndex = taskGroups.findIndex(g => g.id === groupId);
        if (currentIndex === -1) return;
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= taskGroups.length) return;

        const reordered = [...taskGroups];
        const [moved] = reordered.splice(currentIndex, 1);
        reordered.splice(newIndex, 0, moved);

        reorderTaskGroups(reordered);
    };

    // Auto-complete missed recurring tasks on load
    const autoCompletedRef = React.useRef(new Set());
    useEffect(() => {
        if (!tasks.length || !taskStatuses.length) return;
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const missedTasks = tasks.filter(task =>
            task.recurrence_rule &&
            task.due_date &&
            !task.is_completed &&
            new Date(task.due_date) < now &&
            !autoCompletedRef.current.has(task.id)
        );

        if (missedTasks.length === 0) return;

        const sorted = [...taskStatuses].sort((a, b) => a.position - b.position);
        const defaultDone = sorted.find(s => s.is_completed);
        const statusId = defaultDone ? defaultDone.id : null;

        missedTasks.forEach(task => {
            autoCompletedRef.current.add(task.id);
            updateTask(task.id, {
                isCompleted: true,
                statusId: statusId
            });
        });
    }, [tasks, taskStatuses]);

    // Build a flat ordered list of all visible task IDs (parent order, then children inline).
    // Used to find the "task above" for indent operations.
    const buildFlatVisibleOrder = () => {
        const order = [];
        const groupIds = ['uncategorized', ...taskGroups.map(g => g.id)];
        for (const groupId of groupIds) {
            const groupTasks = tasksByGroup[groupId] || [];
            for (const task of groupTasks.filter(t => !t.is_completed)) {
                order.push(task);
                if (task.children) {
                    for (const child of task.children.filter(c => !c.is_completed)) {
                        order.push(child);
                    }
                }
            }
        }
        return order;
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
        setDragDelta(0);
    };

    const handleDragMove = (event) => {
        setDragDelta(event.delta.x);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setDragDelta(0);

        if (!over) return;
        if (filterTags.length > 0) return;

        const activeTask = tasks.find(t => t.id === active.id);
        if (!activeTask) return;

        const deltaX = event.delta.x;

        // --- INDENT: drag right on a top-level task ---
        if (deltaX > INDENT_THRESHOLD_PX && !activeTask.parent_task_id) {
            const flatOrder = buildFlatVisibleOrder();
            const activeIndex = flatOrder.findIndex(t => t.id === activeTask.id);

            // Find the task directly above in the flat visible order that is a top-level task
            // (subtasks cannot parent other subtasks — only 1 level deep)
            let newParent = null;
            for (let i = activeIndex - 1; i >= 0; i--) {
                const candidate = flatOrder[i];
                if (!candidate.parent_task_id) {
                    newParent = candidate;
                    break;
                }
            }

            if (newParent && newParent.id !== activeTask.id) {
                updateTask(activeTask.id, { parentTaskId: newParent.id });
            }
            return;
        }

        // --- OUTDENT: drag left on a subtask ---
        if (deltaX < -INDENT_THRESHOLD_PX && activeTask.parent_task_id) {
            const parentTask = tasks.find(t => t.id === activeTask.parent_task_id);
            // Inherit the parent's section_id when outdenting
            const sectionId = parentTask ? parentTask.section_id : null;
            updateTask(activeTask.id, {
                parentTaskId: null,
                section_id: sectionId || null
            });
            return;
        }

        // --- REORDER ---
        const overTask = tasks.find(t => t.id === over.id);

        // Subtask reorder within the same parent
        if (
            activeTask.parent_task_id &&
            overTask &&
            activeTask.parent_task_id === overTask.parent_task_id &&
            active.id !== over.id
        ) {
            const parentTask = tasks.find(t => t.id === activeTask.parent_task_id);
            if (!parentTask) return;

            // Build the sibling list from the flat tasks array, sorted by sort_order
            const siblings = tasks
                .filter(t => t.parent_task_id === activeTask.parent_task_id && !t.is_completed)
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

            const oldIndex = siblings.findIndex(t => t.id === active.id);
            const newIndex = siblings.findIndex(t => t.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const reordered = arrayMove(siblings, oldIndex, newIndex);
                const groupId = parentTask.section_id || null;
                const updates = reordered.map((t, idx) => ({
                    id: t.id,
                    sortOrder: idx,
                    taskGroupId: groupId
                }));
                reorderTasks(updates);
            }
            return;
        }

        // Top-level task reorder (existing logic)
        let overGroupId = null;

        if (overTask) {
            // If over a subtask, use its parent's group instead
            if (overTask.parent_task_id) {
                const overParent = tasks.find(t => t.id === overTask.parent_task_id);
                overGroupId = overParent ? (overParent.section_id || 'uncategorized') : null;
            } else {
                overGroupId = overTask.section_id || 'uncategorized';
            }
        } else {
            // Check if dropped on a group container
            const overGroup = taskGroups.find(g => g.id === over.id);
            if (overGroup) {
                overGroupId = overGroup.id;
            } else if (over.id === 'uncategorized-drop-zone') {
                overGroupId = 'uncategorized';
            }
        }

        if (!overGroupId) return;

        const activeGroupId = activeTask.section_id || 'uncategorized';

        if (activeGroupId !== overGroupId) {
            // MOVING TO DIFFERENT GROUP
            const destTasks = tasksByGroup[overGroupId] ? [...tasksByGroup[overGroupId]].filter(t => !t.is_completed) : [];

            let insertIndex = destTasks.length;
            if (overTask && !overTask.parent_task_id) {
                const idx = destTasks.findIndex(t => t.id === over.id);
                if (idx !== -1) insertIndex = idx;
            }

            const newDestList = [
                ...destTasks.slice(0, insertIndex),
                { ...activeTask, section_id: overGroupId },
                ...destTasks.slice(insertIndex)
            ];

            const updates = newDestList.map((t, idx) => ({
                id: t.id,
                sortOrder: idx,
                taskGroupId: overGroupId === 'uncategorized' ? null : overGroupId
            }));

            reorderTasks(updates);

        } else {
            // REORDERING IN SAME GROUP
            if (active.id !== over.id) {
                const currentGroupTasks = tasksByGroup[activeGroupId].filter(t => !t.is_completed);
                const oldIndex = currentGroupTasks.findIndex(t => t.id === active.id);
                const newIndex = currentGroupTasks.findIndex(t => t.id === over.id);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newItems = arrayMove(currentGroupTasks, oldIndex, newIndex);
                    const updates = newItems.map((t, idx) => ({
                        id: t.id,
                        sortOrder: idx,
                        taskGroupId: activeGroupId === 'uncategorized' ? null : activeGroupId
                    }));
                    reorderTasks(updates);
                }
            }
        }
    };

    // --- Keyboard indent/outdent/move handlers ---

    const handleIndent = (taskId) => {
        if (filterTags.length > 0) return;

        const task = tasks.find(t => t.id === taskId);
        if (!task || task.parent_task_id) return; // Already a subtask — no sub-sub-tasks

        const flatOrder = buildFlatVisibleOrder();
        const taskIndex = flatOrder.findIndex(t => t.id === taskId);

        let newParent = null;
        for (let i = taskIndex - 1; i >= 0; i--) {
            const candidate = flatOrder[i];
            if (!candidate.parent_task_id) {
                newParent = candidate;
                break;
            }
        }

        if (newParent && newParent.id !== taskId) {
            updateTask(taskId, { parentTaskId: newParent.id });
        }
    };

    const handleOutdent = (taskId) => {
        if (filterTags.length > 0) return;

        const task = tasks.find(t => t.id === taskId);
        if (!task || !task.parent_task_id) return; // Already top-level

        const parentTask = tasks.find(t => t.id === task.parent_task_id);
        const sectionId = parentTask ? parentTask.section_id : null;

        updateTask(taskId, {
            parentTaskId: null,
            section_id: sectionId || null
        });
    };

    const handleMoveUp = (taskId) => {
        if (filterTags.length > 0) return;

        const task = tasks.find(t => t.id === taskId);
        if (!task) return;

        if (task.parent_task_id) {
            // Move up within siblings
            const siblings = tasks
                .filter(t => t.parent_task_id === task.parent_task_id && !t.is_completed)
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

            const currentIndex = siblings.findIndex(t => t.id === taskId);
            if (currentIndex <= 0) return;

            const parentTask = tasks.find(t => t.id === task.parent_task_id);
            const groupId = parentTask ? parentTask.section_id : null;

            const reordered = arrayMove(siblings, currentIndex, currentIndex - 1);
            const updates = reordered.map((t, idx) => ({
                id: t.id,
                sortOrder: idx,
                taskGroupId: groupId
            }));
            reorderTasks(updates);
        } else {
            // Move up within group
            const groupId = task.section_id || 'uncategorized';
            const groupTasks = (tasksByGroup[groupId] || []).filter(t => !t.is_completed);
            const currentIndex = groupTasks.findIndex(t => t.id === taskId);
            if (currentIndex <= 0) return;

            const reordered = arrayMove(groupTasks, currentIndex, currentIndex - 1);
            const updates = reordered.map((t, idx) => ({
                id: t.id,
                sortOrder: idx,
                taskGroupId: groupId === 'uncategorized' ? null : groupId
            }));
            reorderTasks(updates);
        }
    };

    const handleMoveDown = (taskId) => {
        if (filterTags.length > 0) return;

        const task = tasks.find(t => t.id === taskId);
        if (!task) return;

        if (task.parent_task_id) {
            // Move down within siblings
            const siblings = tasks
                .filter(t => t.parent_task_id === task.parent_task_id && !t.is_completed)
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

            const currentIndex = siblings.findIndex(t => t.id === taskId);
            if (currentIndex >= siblings.length - 1) return;

            const parentTask = tasks.find(t => t.id === task.parent_task_id);
            const groupId = parentTask ? parentTask.section_id : null;

            const reordered = arrayMove(siblings, currentIndex, currentIndex + 1);
            const updates = reordered.map((t, idx) => ({
                id: t.id,
                sortOrder: idx,
                taskGroupId: groupId
            }));
            reorderTasks(updates);
        } else {
            // Move down within group
            const groupId = task.section_id || 'uncategorized';
            const groupTasks = (tasksByGroup[groupId] || []).filter(t => !t.is_completed);
            const currentIndex = groupTasks.findIndex(t => t.id === taskId);
            if (currentIndex >= groupTasks.length - 1) return;

            const reordered = arrayMove(groupTasks, currentIndex, currentIndex + 1);
            const updates = reordered.map((t, idx) => ({
                id: t.id,
                sortOrder: idx,
                taskGroupId: groupId === 'uncategorized' ? null : groupId
            }));
            reorderTasks(updates);
        }
    };

    // Compute the full drag projection: type, target, depth, label
    const getDragProjection = () => {
        if (!activeId) return null;
        const activeTask = tasks.find(t => t.id === activeId);
        if (!activeTask) return null;
        if (filterTags.length > 0) return null;

        const currentDepth = activeTask.parent_task_id ? 1 : 0;

        // INDENT: dragging right on a top-level task
        if (dragDelta > INDENT_THRESHOLD_PX && currentDepth === 0) {
            const flatOrder = buildFlatVisibleOrder();
            const activeIndex = flatOrder.findIndex(t => t.id === activeTask.id);
            let newParent = null;
            for (let i = activeIndex - 1; i >= 0; i--) {
                if (!flatOrder[i].parent_task_id) {
                    newParent = flatOrder[i];
                    break;
                }
            }
            if (newParent && newParent.id !== activeTask.id) {
                return {
                    type: 'indent',
                    targetParentId: newParent.id,
                    depth: 1,
                    label: `Subtask of "${newParent.content}"`
                };
            }
        }

        // OUTDENT: dragging left on a subtask
        if (dragDelta < -INDENT_THRESHOLD_PX && currentDepth === 1) {
            const parentTask = tasks.find(t => t.id === activeTask.parent_task_id);
            return {
                type: 'outdent',
                sourceParentId: activeTask.parent_task_id,
                depth: 0,
                label: parentTask ? `Move out of "${parentTask.content}"` : 'Move to top level'
            };
        }

        return null;
    };

    if (!project) return <div className="p-5 text-center text-muted">Select a project to view tasks.</div>;

    const dragProjection = getDragProjection();
    const projectedDepth = dragProjection ? dragProjection.depth : (activeId ? (tasks.find(t => t.id === activeId)?.parent_task_id ? 1 : 0) : 0);

    return (
        <div className="d-flex flex-column h-100 w-100 bg-white">
            <TasksToolbar
                project={project}
                onManageTags={() => setIsManageTags(!isManageTags)}
                tags={tags}
                filterTags={filterTags}
                setFilterTags={setFilterTags}
                onUpdateProject={updateProject}
                auth={auth}
                workspaceId={workspaceId}
                members={members}
            />

            {/* Tag Management Panel */}
            {isManageTags && <TagManager tags={tags} onCreateTag={createTag} onUpdateTag={updateTag} onDeleteTag={deleteTag} />}

            <div className="flex-grow-1 overflow-auto p-4 custom-scrollbar">

                {/* LIST VIEW (Flat-ish but grouped) */}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                >
                    <div className="d-flex flex-column gap-4" style={{ maxWidth: '800px', margin: '0 auto' }}>

                        {/* Add Task Input (Top) */}
                        <div className="bg-white p-3 shadow-sm rounded-3 border">
                            <Form onSubmit={(e) => handleCreateTask(e, null)}>
                                <div className="d-flex gap-2">
                                    <Plus size={20} className="text-muted align-self-center" />
                                    <Form.Control
                                        size="sm"
                                        placeholder="Add a new task..."
                                        value={newTaskContent}
                                        onChange={e => setNewTaskContent(e.target.value)}
                                        className="border-0 shadow-none bg-transparent ps-2 custom-placeholder"
                                        style={{ fontSize: '1rem' }}
                                    />
                                    {newTaskContent && <Button size="sm" type="submit" variant="primary">Add</Button>}
                                </div>
                            </Form>
                        </div>

                        {/* Uncategorized Group */}
                        {tasksByGroup['uncategorized']?.length > 0 && (
                            <div>
                                {taskGroups.length > 0 && <h6 className="fw-bold text-muted mb-2 px-1 small text-uppercase">Tasks</h6>}

                                <SortableContext
                                    items={tasksByGroup['uncategorized']
                                        .filter(t => !t.is_completed)
                                        .map(t => t.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <TaskGroupContainer id="uncategorized-drop-zone">
                                        {tasksByGroup['uncategorized'].filter(t => !t.is_completed).map(task => (
                                            <React.Fragment key={task.id}>
                                                <SortableTaskCard
                                                    task={task}
                                                    viewMode="list"
                                                    onCreateSubtask={(parentId) => handleCreateTask(null, null, parentId)}
                                                    allTags={tags}
                                                    onAssignTag={assignTag}
                                                    onRemoveTag={removeTag}
                                                    onDeleteTask={deleteTask}
                                                    onAddReminder={addReminder}
                                                    onDeleteReminder={deleteReminder}
                                                    onUpdateTask={updateTask}
                                                    taskStatuses={taskStatuses}
                                                    members={members}
                                                    onIndent={handleIndent}
                                                    onOutdent={handleOutdent}
                                                    onMoveUp={handleMoveUp}
                                                    onMoveDown={handleMoveDown}
                                                    dragProjection={dragProjection}
                                                />
                                                {/* Outdent indicator: show after the parent task block */}
                                                {dragProjection?.type === 'outdent' && dragProjection.sourceParentId === task.id && (
                                                    <DropIndicatorLine depth={0} label={dragProjection.label} />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </TaskGroupContainer>
                                </SortableContext>
                            </div>
                        )}

                        {/* Task Groups / Sections */}
                        {taskGroups.map((group, groupIndex) => (
                            <div key={group.id}>
                                <div className="d-flex align-items-center justify-content-between mb-2 px-1 group-header-row">
                                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                                        {/* Section reorder controls */}
                                        <div className="d-flex flex-column section-reorder-controls" style={{ marginRight: '2px' }}>
                                            <ChevronUp
                                                size={12}
                                                className={`cursor-pointer ${groupIndex === 0 ? 'text-muted opacity-25' : 'text-muted hover-text-dark'}`}
                                                onClick={() => groupIndex > 0 && handleMoveSection(group.id, 'up')}
                                                title="Move section up"
                                            />
                                            <ChevronDown
                                                size={12}
                                                className={`cursor-pointer ${groupIndex === taskGroups.length - 1 ? 'text-muted opacity-25' : 'text-muted hover-text-dark'}`}
                                                onClick={() => groupIndex < taskGroups.length - 1 && handleMoveSection(group.id, 'down')}
                                                title="Move section down"
                                            />
                                        </div>

                                        {editingGroupId === group.id ? (
                                            <Form.Control
                                                value={editingGroupName}
                                                onChange={(e) => setEditingGroupName(e.target.value)}
                                                onBlur={saveEditingGroup}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEditingGroup();
                                                    if (e.key === 'Escape') setEditingGroupId(null);
                                                }}
                                                autoFocus
                                                size="sm"
                                                className="fw-bold m-0 p-0 border-0 shadow-none bg-light"
                                                style={{ width: 'auto', maxWidth: '300px' }}
                                            />
                                        ) : (
                                            <h6
                                                className="fw-bold text-muted m-0 small text-uppercase cursor-pointer hover-text-dark"
                                                onClick={() => startEditingGroup(group)}
                                                title="Click to rename section"
                                            >
                                                {group.name}
                                            </h6>
                                        )}

                                        {editingGroupId !== group.id && (
                                            <Trash2
                                                size={14}
                                                className="text-muted cursor-pointer hover-text-danger group-delete-icon"
                                                style={{ opacity: 0.5 }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm(`Delete section "${group.name}"? Tasks will be moved to uncategorized.`)) {
                                                        deleteTaskGroup(group.id);
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                    <span className="badge bg-light text-dark border rounded-pill">
                                        {tasksByGroup[group.id]?.filter(t => !t.is_completed).length || 0}
                                    </span>
                                </div>

                                <SortableContext
                                    items={tasksByGroup[group.id]
                                        ?.filter(t => !t.is_completed)
                                        .map(t => t.id) || []}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <TaskGroupContainer id={group.id}>
                                        {tasksByGroup[group.id]?.filter(t => !t.is_completed).map(task => (
                                            <React.Fragment key={task.id}>
                                                <SortableTaskCard
                                                    task={task}
                                                    viewMode="list"
                                                    onCreateSubtask={(parentId) => handleCreateTask(null, null, parentId)}
                                                    allTags={tags}
                                                    onAssignTag={assignTag}
                                                    onRemoveTag={removeTag}
                                                    onDeleteTask={deleteTask}
                                                    onAddReminder={addReminder}
                                                    onDeleteReminder={deleteReminder}
                                                    onUpdateTask={updateTask}
                                                    taskStatuses={taskStatuses}
                                                    members={members}
                                                    onIndent={handleIndent}
                                                    onOutdent={handleOutdent}
                                                    onMoveUp={handleMoveUp}
                                                    onMoveDown={handleMoveDown}
                                                    dragProjection={dragProjection}
                                                />
                                                {/* Outdent indicator: show after the parent task block */}
                                                {dragProjection?.type === 'outdent' && dragProjection.sourceParentId === task.id && (
                                                    <DropIndicatorLine depth={0} label={dragProjection.label} />
                                                )}
                                            </React.Fragment>
                                        ))}
                                        <div
                                            className="p-2 text-center text-muted small bg-light border-top"
                                            style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                                            onClick={() => {
                                                const content = window.prompt(`Task for ${group.name}:`);
                                                if (content) createTask({ projectId: project.id, taskGroupId: group.id, content });
                                            }}
                                        >
                                            <Plus size={14} className="me-1" /> Add to {group.name}
                                        </div>
                                    </TaskGroupContainer>
                                </SortableContext>
                            </div>
                        ))}

                        <DragOverlay>
                            {activeId ? (
                                <div style={{ transform: 'scale(1.05)', cursor: 'grabbing' }}>
                                    {/* Indent level indicator bar */}
                                    <div
                                        className="drag-indent-indicator"
                                        style={{ marginLeft: projectedDepth > 0 ? '32px' : '0px' }}
                                    />
                                    <TaskCard
                                        task={tasks.find(t => t.id === activeId)}
                                        completed={false}
                                        viewMode="list"
                                        onCreateSubtask={() => { }}
                                        allTags={tags}
                                        onAddReminder={addReminder}
                                        onDeleteReminder={deleteReminder}
                                        onUpdateTask={updateTask}
                                        taskStatuses={taskStatuses}
                                        members={members}
                                    />
                                </div>
                            ) : null}
                        </DragOverlay>

                        {/* Add Task Group Button */}
                        <div className="mt-2 text-center">
                            {isAddingTaskGroup ? (
                                <Form onSubmit={handleCreateTaskGroup} className="d-flex gap-2 mx-auto" style={{ maxWidth: '300px' }}>
                                    <Form.Control size="sm" placeholder="New Group Name" value={newTaskGroupName} onChange={e => setNewTaskGroupName(e.target.value)} autoFocus />
                                    <Button size="sm" type="submit">Add</Button>
                                    <Button size="sm" variant="light" onClick={() => setIsAddingTaskGroup(false)}>X</Button>
                                </Form>
                            ) : (
                                <Button variant="link" className="text-decoration-none text-muted p-0 small fw-bold" onClick={() => setIsAddingTaskGroup(true)}>
                                    + Add Section
                                </Button>
                            )}
                        </div>

                        {/* Completed */}
                        {filteredTasks.some(t => t.is_completed) && (
                            <div className="mt-4 opacity-75">
                                <h6 className="fw-bold small border-bottom pb-2 mb-3">Completed</h6>
                                <div className="d-flex flex-column gap-0 bg-light border rounded">
                                    {filteredTasks.filter(t => t.is_completed).map(task => (
                                        <TaskCard
                                            key={task.id}
                                            task={task}
                                            completed
                                            viewMode="list"
                                            allTags={tags}
                                            onAssignTag={assignTag}
                                            onRemoveTag={removeTag}
                                            onDeleteTask={deleteTask}
                                            onAddReminder={addReminder}
                                            onDeleteReminder={deleteReminder}
                                            onUpdateTask={updateTask}
                                            taskStatuses={taskStatuses}
                                            members={members}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </DndContext>
            </div>
        </div>
    );
};

export default TaskListView;
